import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Mail, Instagram, MapPin, ArrowUpRight, Check, Phone, Globe } from "lucide-react";
import { toast } from "sonner";

const EVENT_TYPES = [
    { value: "portraits", label: "Portrait Session" },
    { value: "gatherings", label: "Large Gathering / Event" },
    { value: "cars", label: "Car Photography" },
    { value: "other", label: "Something Else" },
];

const FALLBACK_CONTACT = {
    email: "hello@willmaxwellphoto.com",
    phone: "",
    city: "Austin",
    region: "Texas · Available for travel",
    studio_hours: "Mon – Fri · 9am – 6pm CT\nWeekends by appointment only",
    socials: [],
};

const SOCIAL_ICON = {
    instagram: Instagram,
    "x / twitter": (props) => <span {...props} className={`${props.className} font-serif`}>𝕏</span>,
    twitter: (props) => <span {...props} className={`${props.className} font-serif`}>𝕏</span>,
    tiktok: Globe,
    facebook: Globe,
    youtube: Globe,
    threads: Globe,
};

function iconFor(label) {
    return SOCIAL_ICON[(label || "").toLowerCase().trim()] || Globe;
}

export default function Contact() {
    const [contactInfo, setContactInfo] = useState(FALLBACK_CONTACT);
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
        event_type: "portraits",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/site-content")
            .then((res) => {
                if (res.data?.contact) setContactInfo({ ...FALLBACK_CONTACT, ...res.data.contact });
            })
            .catch(() => { /* fallback already set */ });
    }, []);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.name || !form.email || !form.message) {
            setError("Please fill in your name, email, and a short message.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            setSubmitted(true);
            toast.success("Message sent — I'll be in touch soon.");
            setForm({ name: "", email: "", subject: "", message: "", event_type: "portraits" });
        } catch (e2) {
            const msg = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="bg-[#F9F8F6]">
            {/* Header */}
            <section className="border-b border-[#E5DED5]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12 md:pb-16">
                    <p className="overline" data-testid="contact-eyebrow">Get In Touch</p>
                    <h1
                        data-testid="contact-headline"
                        className="font-serif font-light text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight mt-6"
                    >
                        Say <em className="italic text-[#A0522D]">hello</em>.
                    </h1>
                    <p className="mt-6 max-w-2xl text-[#5C5853] text-base md:text-lg leading-relaxed">
                        Tell me about your project, your date, your car, your guest count — whatever you have. I read every message and reply within 48 hours.
                    </p>
                </div>
            </section>

            {/* Content */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                {/* Form */}
                <div className="lg:col-span-7">
                    <p className="overline mb-6" data-testid="contact-form-eyebrow">The Form</p>
                    {submitted ? (
                        <div
                            data-testid="contact-success"
                            className="border border-[#E5DED5] p-8 bg-[#F2EFE9]/50 flex items-start gap-4"
                        >
                            <Check className="w-6 h-6 text-[#2D4A22] flex-shrink-0 mt-1" />
                            <div>
                                <p className="font-serif text-2xl">Message received.</p>
                                <p className="text-sm text-[#5C5853] mt-2">
                                    Thanks for reaching out. I'll be back to you within 48 hours{contactInfo.email ? <> from <span className="font-semibold">{contactInfo.email}</span></> : null}.
                                </p>
                                <button
                                    data-testid="contact-send-another"
                                    onClick={() => setSubmitted(false)}
                                    className="btn-editorial mt-6"
                                >
                                    Send another
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-7">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                                <div>
                                    <label className="overline">Your Name</label>
                                    <input
                                        data-testid="contact-input-name"
                                        type="text"
                                        className="input-underline"
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="Jane Doe"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="overline">Email</label>
                                    <input
                                        data-testid="contact-input-email"
                                        type="email"
                                        className="input-underline"
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="you@email.com"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="overline">I'm interested in</label>
                                <select
                                    data-testid="contact-input-event-type"
                                    value={form.event_type}
                                    onChange={update("event_type")}
                                    className="input-underline appearance-none cursor-pointer"
                                >
                                    {EVENT_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="overline">Subject (optional)</label>
                                <input
                                    data-testid="contact-input-subject"
                                    type="text"
                                    className="input-underline"
                                    value={form.subject}
                                    onChange={update("subject")}
                                    placeholder="Spring wedding in Marfa"
                                />
                            </div>

                            <div>
                                <label className="overline">Message</label>
                                <textarea
                                    data-testid="contact-input-message"
                                    rows={6}
                                    className="input-underline resize-none"
                                    value={form.message}
                                    onChange={update("message")}
                                    placeholder="Tell me about your project, date, and location…"
                                    required
                                />
                            </div>

                            {error && (
                                <p data-testid="contact-error" className="text-sm text-[#7A2E2E]">{error}</p>
                            )}

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit-button"
                                className="btn-editorial primary disabled:opacity-60"
                            >
                                {submitting ? "Sending…" : "Send message"} <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </form>
                    )}
                </div>

                {/* Info */}
                <aside className="lg:col-span-5">
                    <div className="border border-[#E5DED5] bg-[#F2EFE9]/40 p-8 md:p-10 space-y-8">
                        <div>
                            <p className="overline">Direct</p>
                            <div className="mt-4 space-y-4">
                                {contactInfo.email && (
                                    <a
                                        href={`mailto:${contactInfo.email}`}
                                        data-testid="contact-email-link"
                                        className="flex items-start gap-3 link-underline"
                                    >
                                        <Mail className="w-5 h-5 mt-1 text-[#A0522D]" />
                                        <div>
                                            <p className="font-serif text-xl leading-none break-all">{contactInfo.email}</p>
                                            <p className="text-xs text-[#5C5853] mt-1">Replies within 48 hours</p>
                                        </div>
                                    </a>
                                )}
                                {contactInfo.phone && (
                                    <a
                                        href={`tel:${contactInfo.phone.replace(/\s+/g, "")}`}
                                        data-testid="contact-phone-link"
                                        className="flex items-start gap-3 link-underline"
                                    >
                                        <Phone className="w-5 h-5 mt-1 text-[#A0522D]" />
                                        <div>
                                            <p className="font-serif text-xl leading-none">{contactInfo.phone}</p>
                                            <p className="text-xs text-[#5C5853] mt-1">Call or text</p>
                                        </div>
                                    </a>
                                )}
                                {(contactInfo.city || contactInfo.region) && (
                                    <div className="flex items-start gap-3">
                                        <MapPin className="w-5 h-5 mt-1 text-[#A0522D]" />
                                        <div data-testid="contact-location">
                                            {contactInfo.city && (
                                                <p className="font-serif text-xl leading-none">{contactInfo.city}</p>
                                            )}
                                            {contactInfo.region && (
                                                <p className="text-xs text-[#5C5853] mt-1">{contactInfo.region}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {contactInfo.socials && contactInfo.socials.length > 0 && (
                            <div className="border-t border-[#E5DED5] pt-8">
                                <p className="overline">Social</p>
                                <ul className="mt-4 space-y-3" data-testid="contact-socials">
                                    {contactInfo.socials.map((s, i) => {
                                        const Icon = iconFor(s.label);
                                        return (
                                            <li key={`${s.label}-${i}`}>
                                                <a
                                                    href={s.url || "#"}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`contact-social-${(s.label || "link").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                    className="link-underline inline-flex items-center gap-3"
                                                >
                                                    <Icon className="w-5 h-5" />
                                                    <span>
                                                        <span className="overline mr-2 align-middle">{s.label}</span>
                                                        {s.handle}
                                                    </span>
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        {contactInfo.studio_hours && (
                            <div className="border-t border-[#E5DED5] pt-8">
                                <p className="overline">Studio Hours</p>
                                <p data-testid="contact-studio-hours" className="text-sm text-[#1A1817] mt-3 leading-relaxed whitespace-pre-wrap">
                                    {contactInfo.studio_hours}
                                </p>
                            </div>
                        )}
                    </div>
                </aside>
            </section>
        </div>
    );
}
